/**
 * ScheduledMessagesBar
 *
 * The strip of pending scheduled sends for a contact, docked just above the
 * Composer. Lists each queued SMS/email with its send time + preview and lets a
 * recruiter edit (body/subject/time) or cancel while it is still 'scheduled'. A
 * row mid-send ('sending') is read-only. Renders nothing when the queue is empty.
 *
 * Refreshes when `reloadToken` changes — the parent bumps it after a new
 * schedule (Composer.onScheduled) and on the Realtime ping that fires when the
 * worker sends a due message (so a fired row drops off the list live).
 */
import { useCallback, useEffect, useState } from 'react';
import { Clock, X, Pencil, Loader2, MessageSquare, Mail, AlertTriangle } from 'lucide-react';
import api from '../../services/api';
import { ScheduledMessage } from '../types';

interface Props {
  contactId: string;
  /** Bumped by the parent (new schedule + Realtime ping) so the list re-fetches. */
  reloadToken: number;
  /** Optional: called after a cancel/edit so the parent can refresh peers. */
  onChanged?: () => void;
}

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// datetime-local values are LOCAL wall-clock (no timezone).
function toLocalInputValue(d: Date): string {
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export default function ScheduledMessagesBar({ contactId, reloadToken, onChanged }: Props) {
  const [messages, setMessages] = useState<ScheduledMessage[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const [editBody, setEditBody] = useState('');
  const [editSubject, setEditSubject] = useState('');
  const [editAt, setEditAt] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/api/crm/contacts/${contactId}/scheduled-messages`);
      setMessages(r.data?.messages || []);
    } catch {
      /* best-effort strip — the composer still works without it */
    }
  }, [contactId]);

  useEffect(() => { load(); }, [load, reloadToken]);

  const startEdit = (m: ScheduledMessage) => {
    setEditing(m.id);
    setEditBody(m.body || '');
    setEditSubject(m.subject || '');
    setEditAt(toLocalInputValue(new Date(m.send_at)));
    setErr(null);
  };

  const editDate = editAt ? new Date(editAt) : null;
  const editInvalid = !editDate || Number.isNaN(editDate.getTime());
  const editInPast = !editInvalid && editDate!.getTime() <= Date.now();
  const editInQuiet = !editInvalid && (editDate!.getHours() >= 21 || editDate!.getHours() < 8);

  // A file-only SMS (attachment, no typed body) is a valid message — its secure
  // link IS the content — so it must not be forced to carry body text to edit.
  const isFileOnlySms = (m: ScheduledMessage) => m.channel === 'sms' && (m.study_document_ids?.length ?? 0) > 0;

  // Save is blocked for the same reasons the composer blocks a schedule (SMS
  // respects quiet hours; email needs a subject; future only). Body is required
  // UNLESS this is a file-only SMS.
  const editBlockedFor = (m: ScheduledMessage) =>
    editInvalid || editInPast
    || (!editBody.trim() && !isFileOnlySms(m))
    || (m.channel === 'sms' && editInQuiet)
    || (m.channel === 'email' && !editSubject.trim());

  const saveEdit = async (m: ScheduledMessage) => {
    if (editBlockedFor(m)) return;
    setBusy(m.id); setErr(null);
    try {
      // Omit body when empty (a file-only SMS reschedule): the backend rejects
      // an empty body and never touches study_document_ids, so the attachment
      // and its empty body are preserved.
      await api.patch(`/api/crm/contacts/${contactId}/scheduled-messages/${m.id}`, {
        ...(editBody.trim() ? { body: editBody.trim() } : {}),
        ...(m.channel === 'email' ? { subject: editSubject.trim() } : {}),
        send_at: new Date(editAt).toISOString(),
      });
      setEditing(null);
      await load();
      onChanged?.();
    } catch (e: any) {
      setErr(e?.response?.data?.reason || e?.response?.data?.error || 'Could not update.');
    } finally {
      setBusy(null);
    }
  };

  const cancel = async (m: ScheduledMessage) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Cancel this scheduled message?')) return;
    setBusy(m.id); setErr(null);
    try {
      await api.delete(`/api/crm/contacts/${contactId}/scheduled-messages/${m.id}`);
      await load();
      onChanged?.();
    } catch (e: any) {
      setErr(e?.response?.data?.reason || e?.response?.data?.error || 'Could not cancel.');
    } finally {
      setBusy(null);
    }
  };

  if (!messages.length) return null;

  return (
    <div className="border-t border-primary-100 bg-primary-50/40 px-3 py-2 dark:border-primary-900/40 dark:bg-primary-900/10">
      <div className="mb-1 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-primary-700 dark:text-primary-300">
        <Clock className="h-3 w-3" /> Scheduled ({messages.length})
      </div>
      <ul className="space-y-1.5">
        {messages.map((m) => {
          const isEditing = editing === m.id;
          const isSending = m.status === 'sending';
          return (
            <li key={m.id} className="rounded-lg border border-primary-200 bg-white px-2.5 py-1.5 text-xs dark:border-primary-900/50 dark:bg-gray-800">
              {!isEditing ? (
                <div className="flex items-start gap-2">
                  {m.channel === 'email'
                    ? <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary-600" />
                    : <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary-600" />}
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-700 dark:text-gray-200">
                      {fmtWhen(m.send_at)}
                      {isSending && <span className="ml-1.5 text-primary-600">· sending…</span>}
                    </div>
                    {m.channel === 'email' && m.subject && (
                      <div className="truncate font-semibold text-gray-600 dark:text-gray-300">{m.subject}</div>
                    )}
                    <div className="truncate text-gray-500 dark:text-gray-400">
                      {m.body || (m.study_document_ids?.length ? '(file only)' : '')}
                    </div>
                  </div>
                  {!isSending && (
                    <div className="flex shrink-0 items-center gap-0.5">
                      <button
                        type="button" onClick={() => startEdit(m)} aria-label="Edit scheduled message" title="Edit"
                        className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-primary-700 dark:hover:bg-gray-700"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button" onClick={() => cancel(m)} disabled={busy === m.id} aria-label="Cancel scheduled message" title="Cancel"
                        className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-red-600 disabled:opacity-50 dark:hover:bg-gray-700"
                      >
                        {busy === m.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-1.5">
                  {m.channel === 'email' && (
                    <input
                      value={editSubject} onChange={(e) => setEditSubject(e.target.value)} placeholder="Subject…" maxLength={200}
                      className="w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                    />
                  )}
                  <textarea
                    value={editBody} onChange={(e) => setEditBody(e.target.value)} rows={2}
                    placeholder={isFileOnlySms(m) ? 'Optional message (a file is attached)…' : 'Message…'}
                    className="w-full resize-none rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                  />
                  <input
                    type="datetime-local" value={editAt} min={toLocalInputValue(new Date())} onChange={(e) => setEditAt(e.target.value)}
                    className="w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                  />
                  {m.channel === 'sms' && editInQuiet && !editInvalid && (
                    <p className="flex items-start gap-1 text-amber-600 dark:text-amber-400">
                      <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                      <span>Inside quiet hours (9pm–8am) — pick a time between 8am and 9pm.</span>
                    </p>
                  )}
                  {editInPast && !editInvalid && (
                    <p className="text-amber-600 dark:text-amber-400">Pick a time in the future.</p>
                  )}
                  <div className="flex items-center justify-end gap-2">
                    <button type="button" onClick={() => setEditing(null)} className="rounded px-2 py-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                      Cancel
                    </button>
                    <button
                      type="button" onClick={() => saveEdit(m)} disabled={busy === m.id || editBlockedFor(m)}
                      className="inline-flex items-center gap-1 rounded-md bg-primary-600 px-2.5 py-1 font-semibold text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {busy === m.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Save
                    </button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {err && <p className="mt-1 text-[11px] text-red-600 dark:text-red-400">{err}</p>}
    </div>
  );
}
